import { For, Show, createMemo } from "solid-js";
import type { MannequinModel } from "@tailor-frame/engine";

export type MannequinSex = "female" | "male";
export type MannequinView = "front" | "side" | "back";

export const mannequinViews: Array<{ id: MannequinView; label: string }> = [
  { id: "front", label: "Front" },
  { id: "side", label: "Side (out)" },
  { id: "back", label: "Back" },
];

type Point = [number, number];

/** Smooth open curve through every point (Catmull-Rom expressed as cubic Béziers). */
function smoothThrough(points: Point[]): string {
  let path = "";
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, points.length - 1)];
    path += ` C ${p1[0] + (p2[0] - p0[0]) / 6} ${p1[1] + (p2[1] - p0[1]) / 6} ${p2[0] - (p3[0] - p1[0]) / 6} ${p2[1] - (p3[1] - p1[1]) / 6} ${p2[0]} ${p2[1]}`;
  }
  return path;
}

/** Leg cross-section at `t` along a segment: half-widths (head units) to the +x side and the -x side of the bone axis. */
type LegStation = [t: number, plus: number, minus: number];
interface LegProfile { thigh: LegStation[]; calf: LegStation[] }

/*
 * Front/back: +x is the outside of the (right) leg. Outer thigh peaks high
 * (vastus lateralis, and the female saddle area); the inner thigh carries the
 * adductors and, on the male figure, the teardrop vastus medialis just above
 * the knee. The inner calf (medial gastrocnemius) sits lower and fuller than
 * the outer. The outer ankle bone is lower than the inner.
 * Side: the face looks to -x, so +x is the back of the leg (seat, hamstring,
 * calf) and -x the front (quadriceps, kneecap, shin).
 */
const legProfiles: Record<"front" | "side", Record<MannequinSex, LegProfile>> = {
  front: {
    male: {
      thigh: [[-0.12, 0.4, 0.3], [0, 0.42, 0.33], [0.18, 0.43, 0.31], [0.42, 0.37, 0.26], [0.68, 0.29, 0.21], [0.86, 0.23, 0.22], [1, 0.2, 0.18]],
      calf: [[0.08, 0.19, 0.17], [0.28, 0.24, 0.22], [0.42, 0.22, 0.24], [0.62, 0.16, 0.18], [0.82, 0.11, 0.11], [0.93, 0.1, 0.1], [1, 0.11, 0.1]],
    },
    female: {
      thigh: [[-0.12, 0.45, 0.31], [0, 0.47, 0.34], [0.2, 0.46, 0.32], [0.45, 0.37, 0.26], [0.7, 0.27, 0.2], [0.88, 0.2, 0.18], [1, 0.18, 0.16]],
      calf: [[0.08, 0.17, 0.15], [0.3, 0.2, 0.18], [0.44, 0.19, 0.19], [0.64, 0.13, 0.14], [0.84, 0.09, 0.09], [0.94, 0.08, 0.08], [1, 0.09, 0.08]],
    },
  },
  side: {
    male: {
      thigh: [[-0.12, 0.44, 0.32], [0, 0.42, 0.35], [0.28, 0.36, 0.39], [0.58, 0.3, 0.34], [0.84, 0.21, 0.26], [1, 0.19, 0.23]],
      calf: [[0.1, 0.19, 0.2], [0.3, 0.3, 0.17], [0.46, 0.27, 0.15], [0.7, 0.15, 0.12], [0.88, 0.1, 0.11], [1, 0.12, 0.12]],
    },
    female: {
      thigh: [[-0.12, 0.5, 0.32], [0, 0.47, 0.35], [0.28, 0.38, 0.37], [0.58, 0.29, 0.31], [0.84, 0.19, 0.23], [1, 0.17, 0.21]],
      calf: [[0.1, 0.17, 0.18], [0.32, 0.26, 0.15], [0.48, 0.23, 0.13], [0.72, 0.12, 0.1], [0.9, 0.085, 0.095], [1, 0.1, 0.1]],
    },
  },
};

/**
 * One leg as a single silhouette from hip to floor, drawn for the +x leg and
 * mirrored with `mirror`. Thigh widths are stretched near the hip so the leg
 * meets the hip block whatever seat measurement drives it.
 */
function LegForm(props: { hip: Point; knee: Point; ankle: Point; ground: number; h: number; profile: LegProfile; side: boolean; female: boolean; thighScale: number; mirror?: boolean }) {
  const h = props.h;
  const sx = props.mirror ? -1 : 1;
  const thigh = stationPoints(props.hip, props.knee, props.profile.thigh, (t) => h * (1 + (props.thighScale - 1) * Math.max(0, 1 - Math.max(t, 0) * 1.4)));
  const calf = stationPoints(props.knee, props.ankle, props.profile.calf, () => h);
  const plus = [...thigh, ...calf].map((s) => s.plus);
  const minus = [...thigh, ...calf].map((s) => s.minus);
  const lastPlus = plus[plus.length - 1];
  const lastMinus = minus[minus.length - 1];
  const [ax, ay] = props.ankle;
  const foot = (props.female ? 0.9 : 1) * h;
  const drop = props.ground - ay;
  // Foot: side view looks to -x with a rounded heel, arch and toe spring;
  // front/back shows a slightly turned-out foot widening to the toes.
  const g = props.ground;
  const footPath = props.side
    ? ` C ${lastPlus[0] - foot * 0.01} ${ay + drop * 0.3} ${ax + foot * 0.21} ${g - drop * 0.5} ${ax + foot * 0.19} ${g - drop * 0.18}
        Q ${ax + foot * 0.17} ${g} ${ax + foot * 0.06} ${g}
        L ${ax - foot * 0.62} ${g}
        Q ${ax - foot * 0.8} ${g} ${ax - foot * 0.84} ${g - drop * 0.12}
        Q ${ax - foot * 0.85} ${g - drop * 0.26} ${ax - foot * 0.7} ${g - drop * 0.3}
        L ${ax - foot * 0.56} ${g - drop * 0.36}
        C ${ax - foot * 0.3} ${g - drop * 0.5} ${lastMinus[0] - foot * 0.04} ${ay + drop * 0.3} ${lastMinus[0]} ${lastMinus[1]}`
    : ` C ${lastPlus[0] + foot * 0.06} ${ay + drop * 0.25} ${ax + foot * 0.14} ${ay + drop * 0.45} ${ax + foot * 0.2} ${g - drop * 0.25}
        Q ${ax + foot * 0.26} ${g} ${ax + foot * 0.14} ${g}
        L ${ax - foot * 0.1} ${g}
        Q ${ax - foot * 0.2} ${g} ${ax - foot * 0.16} ${g - drop * 0.3}
        C ${ax - foot * 0.12} ${ay + drop * 0.4} ${lastMinus[0] - foot * 0.03} ${ay + drop * 0.2} ${lastMinus[0]} ${lastMinus[1]}`;
  // Left open at the top: the thigh runs up under the hip block, which draws the groin and seat.
  const outline = `M ${plus[0][0]} ${plus[0][1]}${smoothThrough(plus)}${footPath}${smoothThrough([...minus].reverse())}`;
  const axis = `M ${props.hip[0]} ${props.hip[1]} L ${props.knee[0]} ${props.knee[1]} L ${ax} ${ay}`;
  return <g class="limb-segment leg-form" transform={`scale(${sx} 1)`}><path d={outline} /><path class="limb-axis" d={axis} /></g>;
}

/** Arm cross-section at `t`: half-widths as fractions of the upper-arm width, to the +x and -x side of the bone axis. */
type ArmStation = [t: number, plus: number, minus: number];
interface ArmProfile { upperArm: ArmStation[]; forearm: ArmStation[] }

/*
 * Front/back: +x is the outside of the (right) arm. The deltoid caps the
 * shoulder and thins to its insertion a third of the way down; the biceps
 * fills the inside of the upper arm; below the elbow the brachioradialis
 * (outside) and flexors (inside) make the forearm widest just under the
 * elbow before it tapers to the wrist.
 * Side: +x is the back of the arm (triceps, high) and -x the front (biceps, lower).
 */
const armProfiles: Record<"front" | "side", Record<MannequinSex, ArmProfile>> = {
  front: {
    male: {
      upperArm: [[-0.06, 0.5, 0.2], [0.1, 0.62, 0.36], [0.3, 0.58, 0.47], [0.46, 0.48, 0.5], [0.62, 0.46, 0.5], [0.84, 0.42, 0.41], [1, 0.41, 0.37]],
      forearm: [[0.1, 0.45, 0.37], [0.24, 0.48, 0.41], [0.45, 0.4, 0.35], [0.7, 0.3, 0.27]],
    },
    female: {
      upperArm: [[-0.06, 0.46, 0.2], [0.1, 0.55, 0.36], [0.32, 0.53, 0.47], [0.56, 0.48, 0.47], [0.82, 0.41, 0.4], [1, 0.38, 0.35]],
      forearm: [[0.1, 0.41, 0.35], [0.26, 0.42, 0.37], [0.5, 0.35, 0.31], [0.74, 0.28, 0.25]],
    },
  },
  side: {
    male: {
      upperArm: [[-0.06, 0.44, 0.44], [0.1, 0.56, 0.52], [0.32, 0.58, 0.48], [0.55, 0.47, 0.56], [0.8, 0.39, 0.46], [1, 0.38, 0.4]],
      forearm: [[0.1, 0.38, 0.42], [0.24, 0.4, 0.46], [0.46, 0.35, 0.37], [0.72, 0.28, 0.27]],
    },
    female: {
      upperArm: [[-0.06, 0.42, 0.42], [0.1, 0.52, 0.48], [0.34, 0.54, 0.47], [0.58, 0.46, 0.5], [0.8, 0.38, 0.42], [1, 0.36, 0.38]],
      forearm: [[0.1, 0.36, 0.38], [0.26, 0.38, 0.41], [0.5, 0.33, 0.34], [0.74, 0.26, 0.25]],
    },
  },
};

/** Points either side of a bone axis from `a` to `b`, `scale` mm per profile unit. */
function stationPoints(a: Point, b: Point, stations: Array<[number, number, number]>, scale: (t: number) => number) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = Math.hypot(dx, dy) || 1;
  const nx = dy / length;
  const ny = -dx / length;
  return stations.map(([t, plus, minus]) => {
    const k = scale(t);
    const cx = a[0] + dx * t;
    const cy = a[1] + dy * t;
    return { plus: [cx + nx * plus * k, cy + ny * plus * k] as Point, minus: [cx - nx * minus * k, cy - ny * minus * k] as Point };
  });
}

/**
 * One arm and hand, drawn for the +x arm and mirrored with `mirror`. The
 * forearm tapers to the measured wrist; the hand hangs along the forearm.
 * Front view sees the hand edge-on from the thumb side, side view sees the
 * back of the hand with the thumb forward (-x).
 */
function ArmForm(props: { shoulder: Point; elbow: Point; wrist: Point; width: number; wristWidth: number; h: number; profile: ArmProfile; side: boolean; female: boolean; mirror?: boolean; far?: boolean }) {
  const w = props.width;
  const wristHalf = props.wristWidth / 2;
  const upper = stationPoints(props.shoulder, props.elbow, props.profile.upperArm, () => w);
  const fore = stationPoints(props.elbow, props.wrist, [...props.profile.forearm, [0.9, wristHalf * 1.08 / w, wristHalf * 1.02 / w], [1, wristHalf / w, wristHalf / w]], () => w);
  const plus = [...upper, ...fore].map((s) => s.plus);
  const minus = [...upper, ...fore].map((s) => s.minus);
  const last = minus[minus.length - 1];
  const armPath = `M ${minus[0][0]} ${minus[0][1]} L ${plus[0][0]} ${plus[0][1]}${smoothThrough(plus)} L ${last[0]} ${last[1]}${smoothThrough([...minus].reverse())} Z`;
  const axis = `M ${props.shoulder[0]} ${props.shoulder[1]} L ${props.elbow[0]} ${props.elbow[1]} L ${props.wrist[0]} ${props.wrist[1]}`;

  const s = (props.female ? 0.88 : 1) * props.h;
  const L = s * 0.76;
  const wh = wristHalf;
  const W = s * (props.side ? 0.42 : 0.24);
  const hand = props.side
    ? `M ${wh} 0 C ${wh * 1.1} ${L * 0.15} ${W * 0.5} ${L * 0.26} ${W * 0.5} ${L * 0.45}
        Q ${W * 0.48} ${L * 0.98} ${W * 0.05} ${L} Q ${-W * 0.3} ${L * 0.98} ${-W * 0.36} ${L * 0.6}
        L ${-W * 0.4} ${L * 0.46} Q ${-W * 0.74} ${L * 0.44} ${-W * 0.64} ${L * 0.24} Q ${-W * 0.5} ${L * 0.1} ${-wh} 0 Z`
    : `M ${wh} 0 C ${wh * 1.05} ${L * 0.2} ${W * 0.55} ${L * 0.3} ${W * 0.55} ${L * 0.5}
        Q ${W * 0.5} ${L * 0.95} ${W * 0.05} ${L} Q ${-W * 0.32} ${L * 0.96} ${-W * 0.36} ${L * 0.62}
        L ${-W * 0.48} ${L * 0.42} Q ${-W * 0.66} ${L * 0.24} ${-wh} 0 Z`;
  const fingers = props.side
    ? [[-W * 0.14, L * 0.58, -W * 0.12, L * 0.93], [W * 0.08, L * 0.56, W * 0.08, L * 0.97], [W * 0.29, L * 0.55, W * 0.27, L * 0.92]]
    : [[W * 0.1, L * 0.55, W * 0.08, L * 0.94]];
  const dx = props.wrist[0] - props.elbow[0];
  const dy = props.wrist[1] - props.elbow[1];
  const angle = -Math.atan2(dx, dy) * 180 / Math.PI;
  return (
    <g classList={{ "limb-segment": true, "arm-form": true, "far-side-limb": !!props.far }} transform={props.mirror ? "scale(-1 1)" : undefined}>
      <path d={armPath} />
      <path class="limb-axis" d={axis} />
      <g class="hand-form" transform={`translate(${props.wrist[0]} ${props.wrist[1]}) rotate(${angle})`}>
        <path d={hand} />
        <For each={fingers}>{(f) => <line x1={f[0]} y1={f[1]} x2={f[2]} y2={f[3]} />}</For>
        <line class="hand-axis" x1="0" y1="0" x2="0" y2={L * 0.45} />
      </g>
    </g>
  );
}

/** A measurement drawn on the figure; `active` guides are emphasised and labelled. */
interface Guide {
  id: string;
  label: string;
  path: string;
  labelX: number;
  labelY: number;
  centred?: boolean;
}

/**
 * Proportional mannequin. Head units set the overall figure; the client's body
 * measurements (`body`, mm) and the pattern's own (`values`, mm) drive the
 * matching levels and widths so edits are visible on the figure. Every entry
 * in `values` the figure knows how to place is also drawn as a guide.
 */
export function MannequinConstruction(props: { model: MannequinModel; sex: MannequinSex; view: MannequinView; values?: Record<string, number>; body?: Record<string, number>; active?: string; labels?: Record<string, string> }) {
  const construction = createMemo(() => {
    const measurement = props.model.measurements;
    const female = props.sex === "female";
    const top = 70;
    const ground = top + measurement.height;
    const canvasHeight = Math.max(measurement.height, 2000) + 170;
    const side = props.view === "side";
    const headSpan = side ? measurement.headDepth : measurement.headWidth;
    const craniumHeight = measurement.headHeight * 0.74;
    const sphereRadiusX = headSpan * 0.52;
    const sphereRadiusY = craniumHeight / 2;
    const sphereCenterY = top + sphereRadiusY;
    const sidePlaneX = headSpan * 0.41;
    const jawTop = top + craniumHeight * 0.68;
    const chinY = top + measurement.headHeight;
    const h = measurement.headHeight;
    const atHead = (count: number) => Math.min(top + count * h, ground);
  const landmarks = [
    { count: 0, label: "TOP" }, { count: 1, label: "CHIN" }, { count: 2, label: "CHEST" },
    { count: 3, label: "WAIST" }, { count: 4, label: "CROTCH" }, { count: 5.5, label: "KNEE" },
    { count: 7, label: "FOOT" },
  ].filter((item) => item.count * h <= measurement.height + 1);
  const shoulderY = atHead(1.45);
  // Female: narrower shoulders and ribcage, smaller waist, fuller hips and seat.
  const values = props.values ?? {};
  // Shape reads body measurements under either their body or pattern name.
  const shape: Record<string, number | undefined> = { ...props.body, ...values };
  const sleeveSource = shape.onePieceSleeveLength ?? shape.sleeveLength;
  const shoulderHalfWidth = !side && shape.halfBack ? shape.halfBack * 1.05 : (side ? measurement.headDepth * 0.48 : measurement.headHeight) * (female ? (side ? 0.9 : 0.82) : 1);
  const depth = side ? 0.7 : 1;
  // Nape (C7) sits a little above the shoulder line; length measurements hang from it.
  const napeY = shoulderY - h * 0.12;
  const chestWidth = Math.max((shape.chest ?? measurement.chest) / Math.PI, measurement.headWidth * 1.7) * (female ? 0.9 : 1);
  const chestHalfWidth = (side ? chestWidth * 0.7 : chestWidth) / 2;
  // Chest girth and bust apex sit at the body's chest, two heads down. Scye depth is a
  // pattern armhole depth with ease, so it hangs below this and is drawn as its own length.
  const chestLineY = atHead(2);
  const chestTop = atHead(1.55);
  const chestBottom = atHead(2.9);
  const chestHeight = chestBottom - chestTop;
  const waistY = shape.backNeckToWaist ? napeY + shape.backNeckToWaist : atHead(3);
  const ankleY = atHead(7);
  // Inside leg sets the crotch above the floor; the knee sits a little above halfway down the leg.
  const crotchY = shape.insideLeg ? Math.min(Math.max(ground - shape.insideLeg, waistY + h * 0.5), ankleY - h * 2) : Math.max(atHead(4), waistY + h * 0.6);
  const kneeY = shape.insideLeg ? crotchY + (ankleY - crotchY) * 0.48 : atHead(5.5);
  const hipHalfWidth = shape.seat ? shape.seat / Math.PI / 2 * depth : side ? chestHalfWidth * (female ? 0.86 : 0.66) : female ? Math.max(chestHalfWidth * 1.18, shoulderHalfWidth * 0.9) : chestHalfWidth * 0.84;
  const waistSource = shape.naturalWaist ?? shape.trouserWaist;
  const waistHalfWidth = waistSource ? waistSource / Math.PI / 2 * depth : side ? chestHalfWidth * (female ? 0.5 : 0.56) : chestHalfWidth * (female ? 0.56 : 0.68);
  // Seat (fullest hip) level: about two thirds of the way down the body rise, a little lower on the female figure.
  const seatY = crotchY - (crotchY - waistY) * (female ? 0.28 : 0.32);
  // In side view the face points to -x, so the seat sits back (+x) and the abdomen forward (-x).
  const hipFront = side ? hipHalfWidth * (female ? 0.84 : 0.9) : hipHalfWidth;
  const hipBack = side ? hipHalfWidth * (female ? 1.16 : 1.1) : hipHalfWidth;
  const waistToSeat = seatY - waistY;
  const seatToCrotch = crotchY - seatY;
  // Hip spring as (fraction of waist-to-seat, fraction of the way from waist to hip width).
  // Male: the iliac crest takes most of the spring high up, so the side runs nearly straight
  // to the seat. Female: a longer, rounder curve that keeps widening to the fullest hip.
  // Side: the seat leaves the small of the back slowly then rounds out to the buttock, while
  // the lower abdomen fills out just below the waist and flattens towards the groin.
  const hipSpring: Record<"front" | "back" | "abdomen", Point[]> = {
    front: female ? [[0.3, 0.38], [0.62, 0.8], [0.84, 0.96]] : [[0.3, 0.6], [0.62, 0.9], [0.84, 0.98]],
    back: female ? [[0.3, 0.2], [0.6, 0.66], [0.84, 0.94]] : [[0.3, 0.24], [0.6, 0.7], [0.84, 0.95]],
    abdomen: female ? [[0.3, 0.6], [0.6, 0.92], [0.84, 0.99]] : [[0.3, 0.68], [0.6, 0.97], [0.84, 1]],
  };
  // One half of the hip block from the waist through the fullest hip, then `tail`
  // as (fraction of the full half-width, y) to where the leg takes over.
  const hipSide = (spring: Point[], full: number, tail: Point[]): Point[] => [
    [waistHalfWidth, waistY],
    ...spring.map(([t, k]) => [waistHalfWidth + (full - waistHalfWidth) * k, waistY + waistToSeat * t] as Point),
    [full, seatY],
    ...tail.map(([k, y]) => [full * k, y] as Point),
  ];
  // Front/back: the groin line runs from the outer hip down and in to the crotch, so the
  // block ends in a shallow V and the outer thigh carries on the hip line below it.
  // Side: the seat rolls under to the gluteal fold behind; the groin crease in front rises
  // towards the hip bone, so the underside slopes up from back to front.
  const groin: Point[] = [[0.98, seatY + seatToCrotch * 0.45], [0.62, crotchY - h * 0.02], [0.16, crotchY + h * 0.04]];
  const nearHalf = hipSide(side ? hipSpring.back : hipSpring.front, hipBack, side
    ? [[0.97, seatY + seatToCrotch * 0.55], [0.8, crotchY + h * 0.06]]
    : groin);
  const farHalf = hipSide(side ? hipSpring.abdomen : hipSpring.front, hipFront, side
    ? [[0.95, seatY + seatToCrotch * 0.4]]
    : groin).map(([x, y]) => [-x, y] as Point);
  const nearEnd = nearHalf[nearHalf.length - 1];
  // Back view: the sacral dimples sit a third of the way from waist to seat; the triangle
  // closes just above the fullest hip.
  const sacrumTop = waistY + waistToSeat * 0.32;
  const sacrumApex = seatY - waistToSeat * 0.08;
  const sacrumHalfWidth = waistHalfWidth * (female ? 0.38 : 0.3);
  const hipPath = `M ${farHalf[0][0]} ${farHalf[0][1]}${smoothThrough(farHalf)}
    ${side ? `Q ${-hipFront * 0.2} ${crotchY + h * 0.12}` : `Q 0 ${crotchY + h * 0.08}`} ${nearEnd[0]} ${nearEnd[1]}${smoothThrough([...nearHalf].reverse())} Z`;
  // Arm widths: measured where given, otherwise proportional to the head.
  const armProfile = armProfiles[side ? "side" : "front"][props.sex];
  const upperArmWidth = shape.armCircumference ? shape.armCircumference / Math.PI * (side ? 1.05 : 0.95) : h * (female ? 0.4 : 0.46) * (side ? 1.05 : 1);
  const wristSource = shape.wrist ?? shape.closeWrist;
  const wristWidth = wristSource ? wristSource / Math.PI * (side ? 0.85 : 1.15) : h * (female ? 0.22 : 0.25) * (side ? 0.75 : 1);
  // The shoulder point is the outside of the deltoid, so the arm bone hangs a little inside it.
  const armX = side ? shoulderHalfWidth * 0.15 : shoulderHalfWidth - upperArmWidth * 0.36;
  // Sleeve length runs shoulder point to just past the wrist bone; elbow length to the elbow.
  const wristY = sleeveSource ? shoulderY + sleeveSource * 0.94 : atHead(4);
  const elbowY = shape.elbowLength ? Math.min(shoulderY + shape.elbowLength, wristY - h * 0.6) : shoulderY + (wristY - shoulderY) * 0.55;
  // Female carrying angle: elbows sit slightly further out from the body.
  const elbowX = side ? armX + h * 0.1 : armX + h * (female ? 0.1 : 0.07);
  const wristX = side ? armX - h * 0.03 : armX + h * (female ? 0.06 : 0.03);
  // Female legs: thighs set wider at the hip and angle in towards the knee.
  // Side: the hip joint sits just behind the centre line and the ankle falls under it.
  const thighX = side ? hipHalfWidth * 0.14 : hipHalfWidth * (female ? 0.56 : 0.52);
  const kneeHalfWidth = side ? hipHalfWidth * 0.06 : hipHalfWidth * (female ? 0.34 : 0.4);
  const ankleHalfWidth = side ? hipHalfWidth * 0.1 : hipHalfWidth * (female ? 0.27 : 0.3);
  const legProfile = legProfiles[side ? "side" : "front"][props.sex];
  const thighScale = Math.min(1.4, Math.max(0.8, (hipBack - thighX) / (legProfile.thigh[1][1] * h)));
  const bustY = chestLineY;
  const jawScale = female ? 0.86 : 1;
  // Female face: a smooth oval tapering to a narrow chin; male keeps the square jaw.
  const frontHeadOutline = female ? `M 0 ${top}
    C ${-sidePlaneX} ${top} ${-sidePlaneX} ${top + craniumHeight * 0.22} ${-sidePlaneX} ${jawTop}
    C ${-sidePlaneX} ${top + h * 0.72} ${-headSpan * 0.2} ${chinY - h * 0.02} 0 ${chinY}
    C ${headSpan * 0.2} ${chinY - h * 0.02} ${sidePlaneX} ${top + h * 0.72} ${sidePlaneX} ${jawTop}
    C ${sidePlaneX} ${top + craniumHeight * 0.22} ${sidePlaneX} ${top} 0 ${top} Z` : `M 0 ${top}
    C ${-sidePlaneX} ${top} ${-sidePlaneX} ${top + craniumHeight * 0.22} ${-sidePlaneX} ${jawTop}
    L ${-headSpan * 0.32 * jawScale} ${chinY - measurement.headHeight * 0.2}
    Q ${-headSpan * 0.16 * jawScale} ${chinY} 0 ${chinY}
    Q ${headSpan * 0.16 * jawScale} ${chinY} ${headSpan * 0.32 * jawScale} ${chinY - measurement.headHeight * 0.2}
    L ${sidePlaneX} ${jawTop}
    L ${sidePlaneX} ${top + craniumHeight * 0.22}
    C ${sidePlaneX} ${top} ${sidePlaneX} ${top} 0 ${top} Z`;
  const jawX = headSpan * (female ? 0.12 : 0.18);
  // Side profile (face to -x) through facial landmarks, in head-span (x) and head-height (y) units:
  // forehead, brow ridge, nasion, nose tip, lips, chin, under-jaw, jaw angle, then round the skull.
  const f = (male: number, fem: number) => (female ? fem : male);
  const profilePoints: Point[] = ([
    [0, 0], [-0.2, 0.035], [-0.32, 0.15], [-0.36, 0.29], [f(-0.375, -0.36), 0.375], [-0.345, 0.435],
    [f(-0.42, -0.4), 0.5], [f(-0.5, -0.45), 0.565], [f(-0.44, -0.41), 0.6], [-0.37, 0.61],
    [f(-0.395, -0.405), 0.655], [-0.365, 0.69], [f(-0.385, -0.395), 0.72], [-0.335, 0.775],
    [f(-0.355, -0.315), 0.86], [f(-0.29, -0.25), 0.94], [-0.12, 0.995], [0.04, f(0.93, 0.95)],
    [f(0.18, 0.12), 0.8], [0.3, 0.68], [0.46, 0.54], [0.5, 0.36], [0.43, 0.15], [0.22, 0.025], [0, 0],
  ] as Point[]).map(([x, y]) => [x * headSpan, top + y * h] as Point);
  const sideHeadOutline = `M 0 ${top}${smoothThrough(profilePoints)} Z`;
  const headOutline = side ? sideHeadOutline : frontHeadOutline;
  const rearView = props.view === "back";
  const hs = headSpan;
  const sp = sidePlaneX;
  // Hair. Male: short, side-parted, sideburns. Female: pulled back into a high ponytail.
  const hairCrown = `C ${-sp * 1.12} ${top - h * 0.02} ${-hs * 0.2} ${top - h * 0.06} 0 ${top - h * 0.055} C ${hs * 0.2} ${top - h * 0.06} ${sp * 1.12} ${top - h * 0.02} ${sp * 1.06} ${top + h * 0.3}`;
  const hair = side
    ? female
      ? `M ${-hs * 0.3} ${top + h * 0.22}
        C ${-hs * 0.36} ${top + h * 0.02} ${-hs * 0.12} ${top - h * 0.05} ${hs * 0.05} ${top - h * 0.04}
        C ${hs * 0.32} ${top - h * 0.03} ${hs * 0.52} ${top + h * 0.12} ${hs * 0.54} ${top + h * 0.3}
        Q ${hs * 0.54} ${top + h * 0.55} ${hs * 0.36} ${top + h * 0.72} L ${hs * 0.26} ${top + h * 0.66}
        Q ${hs * 0.3} ${top + h * 0.5} ${hs * 0.25} ${top + h * 0.44} Q ${hs * 0.12} ${top + h * 0.36} ${hs * 0.05} ${top + h * 0.38}
        Q ${-hs * 0.05} ${top + h * 0.4} ${-hs * 0.1} ${top + h * 0.3} Q ${-hs * 0.18} ${top + h * 0.2} ${-hs * 0.3} ${top + h * 0.22} Z`
      : `M ${-hs * 0.3} ${top + h * 0.2}
        C ${-hs * 0.37} ${top - h * 0.02} ${-hs * 0.1} ${top - h * 0.08} ${hs * 0.08} ${top - h * 0.06}
        C ${hs * 0.36} ${top - h * 0.04} ${hs * 0.55} ${top + h * 0.16} ${hs * 0.53} ${top + h * 0.38}
        Q ${hs * 0.5} ${top + h * 0.6} ${hs * 0.4} ${top + h * 0.7} L ${hs * 0.3} ${top + h * 0.66}
        Q ${hs * 0.3} ${top + h * 0.5} ${hs * 0.26} ${top + h * 0.42} Q ${hs * 0.12} ${top + h * 0.36} ${hs * 0.03} ${top + h * 0.4}
        L ${-hs * 0.01} ${top + h * 0.56} L ${-hs * 0.07} ${top + h * 0.56} L ${-hs * 0.08} ${top + h * 0.36}
        Q ${-hs * 0.16} ${top + h * 0.22} ${-hs * 0.3} ${top + h * 0.2} Z`
    : rearView
      ? `M ${-sp * 0.98} ${top + h * 0.5} L ${-sp * 1.06} ${top + h * 0.3} ${hairCrown} L ${sp * 0.98} ${top + h * 0.5}
        Q ${sp * 0.9} ${top + h * 0.68} ${sp * (female ? 0.5 : 0.55)} ${top + h * (female ? 0.76 : 0.74)}
        Q 0 ${top + h * (female ? 0.8 : 0.78)} ${-sp * (female ? 0.5 : 0.55)} ${top + h * (female ? 0.76 : 0.74)}
        Q ${-sp * 0.9} ${top + h * 0.68} ${-sp * 0.98} ${top + h * 0.5} Z`
      : female
        ? `M ${-sp * 1.02} ${top + h * 0.42} L ${-sp * 1.06} ${top + h * 0.3} ${hairCrown} L ${sp * 1.02} ${top + h * 0.42}
          L ${sp * 0.95} ${top + h * 0.4} Q ${sp * 0.95} ${top + h * 0.2} ${hs * 0.14} ${top + h * 0.165} Q ${hs * 0.04} ${top + h * 0.16} 0 ${top + h * 0.18}
          Q ${-hs * 0.04} ${top + h * 0.16} ${-hs * 0.14} ${top + h * 0.165} Q ${-sp * 0.95} ${top + h * 0.2} ${-sp * 0.95} ${top + h * 0.4} Z`
        : `M ${-sp} ${top + h * 0.5} L ${-sp * 1.06} ${top + h * 0.3} ${hairCrown} L ${sp} ${top + h * 0.5}
          L ${sp * 0.92} ${top + h * 0.5} L ${sp * 0.9} ${top + h * 0.25} Q ${sp * 0.6} ${top + h * 0.15} ${hs * 0.12} ${top + h * 0.17}
          Q ${-hs * 0.1} ${top + h * 0.14} ${-sp * 0.9} ${top + h * 0.25} L ${-sp * 0.92} ${top + h * 0.5} Z`;
  // Strand lines give the hair direction.
  const hairStrands = side
    ? female
      ? [`M ${-hs * 0.22} ${top + h * 0.16} Q ${hs * 0.1} ${top} ${hs * 0.5} ${top + h * 0.28}`, `M ${-hs * 0.08} ${top + h * 0.3} Q ${hs * 0.2} ${top + h * 0.1} ${hs * 0.5} ${top + h * 0.3}`, `M ${hs * 0.3} ${top + h * 0.6} Q ${hs * 0.46} ${top + h * 0.45} ${hs * 0.5} ${top + h * 0.32}`]
      : [`M ${-hs * 0.24} ${top + h * 0.12} Q ${hs * 0.05} ${top - h * 0.02} ${hs * 0.35} ${top + h * 0.08}`, `M ${-hs * 0.1} ${top + h * 0.24} Q ${hs * 0.2} ${top + h * 0.1} ${hs * 0.45} ${top + h * 0.3}`, `M ${hs * 0.24} ${top + h * 0.38} Q ${hs * 0.42} ${top + h * 0.45} ${hs * 0.38} ${top + h * 0.64}`]
    : rearView
      ? female
        ? [`M ${-sp * 0.8} ${top + h * 0.6} Q ${-sp * 0.5} ${top + h * 0.45} ${-hs * 0.04} ${top + h * 0.32}`, `M ${sp * 0.8} ${top + h * 0.6} Q ${sp * 0.5} ${top + h * 0.45} ${hs * 0.04} ${top + h * 0.32}`, `M ${-sp * 0.7} ${top + h * 0.08} Q ${-sp * 0.3} ${top + h * 0.12} ${-hs * 0.04} ${top + h * 0.28}`, `M ${sp * 0.7} ${top + h * 0.08} Q ${sp * 0.3} ${top + h * 0.12} ${hs * 0.04} ${top + h * 0.28}`]
        : [`M ${-sp * 0.6} ${top + h * 0.05} Q ${-sp * 0.5} ${top + h * 0.4} ${-sp * 0.4} ${top + h * 0.7}`, `M 0 ${top - h * 0.03} Q ${hs * 0.03} ${top + h * 0.4} 0 ${top + h * 0.74}`, `M ${sp * 0.6} ${top + h * 0.05} Q ${sp * 0.5} ${top + h * 0.4} ${sp * 0.4} ${top + h * 0.7}`]
      : female
        ? [`M 0 ${top + h * 0.18} L 0 ${top - h * 0.04}`, `M ${-hs * 0.08} ${top + h * 0.17} Q ${-sp * 0.7} ${top + h * 0.06} ${-sp} ${top + h * 0.34}`, `M ${hs * 0.08} ${top + h * 0.17} Q ${sp * 0.7} ${top + h * 0.06} ${sp} ${top + h * 0.34}`]
        : [`M ${hs * 0.12} ${top + h * 0.17} Q ${hs * 0.16} ${top + h * 0.02} ${hs * 0.12} ${top - h * 0.05}`, `M ${hs * 0.1} ${top + h * 0.12} Q ${-sp * 0.5} ${top - h * 0.02} ${-sp * 1.02} ${top + h * 0.3}`, `M ${hs * 0.16} ${top + h * 0.12} Q ${sp * 0.6} ${top + h * 0.02} ${sp * 1.02} ${top + h * 0.3}`];
  // Ponytail: tied high at the back of the head, falling to between the shoulder blades.
  const ponytail = !female ? undefined : side
    ? `M ${hs * 0.54} ${top + h * 0.25} C ${hs * 1.02} ${top + h * 0.28} ${hs * 0.92} ${top + h * 0.95} ${hs * 0.62} ${top + h * 1.42}
      Q ${hs * 0.68} ${top + h * 0.95} ${hs * 0.6} ${top + h * 0.62} Q ${hs * 0.56} ${top + h * 0.44} ${hs * 0.53} ${top + h * 0.36} Z`
    : rearView
      ? `M ${-hs * 0.08} ${top + h * 0.32} C ${-hs * 0.24} ${top + h * 0.6} ${-hs * 0.2} ${top + h * 1.1} ${hs * 0.02} ${top + h * 1.55}
        C ${hs * 0.2} ${top + h * 1.1} ${hs * 0.24} ${top + h * 0.6} ${hs * 0.08} ${top + h * 0.32} Z`
      : undefined;
  const hairTie: Point | undefined = !female ? undefined : side ? [hs * 0.55, top + h * 0.3] : rearView ? [0, top + h * 0.32] : undefined;
  const chestPath = `M 0 ${chestTop}
    C ${-chestHalfWidth * 0.88} ${chestTop} ${-chestHalfWidth} ${chestTop + chestHeight * 0.28} ${-chestHalfWidth * 0.92} ${chestTop + chestHeight * 0.48}
    C ${-chestHalfWidth * 0.78} ${chestTop + chestHeight * 0.76} ${-chestHalfWidth * 0.48} ${chestBottom} 0 ${chestBottom}
    C ${chestHalfWidth * 0.48} ${chestBottom} ${chestHalfWidth * 0.78} ${chestTop + chestHeight * 0.76} ${chestHalfWidth * 0.92} ${chestTop + chestHeight * 0.48}
    C ${chestHalfWidth} ${chestTop + chestHeight * 0.28} ${chestHalfWidth * 0.88} ${chestTop} 0 ${chestTop} Z`;

  const label = (id: string, fallback: string) => props.labels?.[id] ?? fallback;
  const across = (id: string, y: number, circumference: number): Guide => {
    const half = circumference / Math.PI / 2 * depth;
    return { id, label: `${label(id, id)} ${circumference} mm`, path: `M ${-half} ${y} H ${half} M ${-half} ${y - 12} V ${y + 12} M ${half} ${y - 12} V ${y + 12}`, labelX: 0, labelY: y - 20, centred: true };
  };
  const drop = (id: string, x: number, length: number): Guide => ({
    id, label: `${label(id, id)} ${length} mm`, path: `M ${x - 12} ${napeY} H ${x + 12} M ${x} ${napeY} V ${napeY + length} M ${x - 12} ${napeY + length} H ${x + 12}`, labelX: x + 14, labelY: napeY + length - 8,
  });
  const guides: Guide[] = [];
  const has = (id: string) => values[id] !== undefined && values[id] > 0;
  const mm = (id: string) => `${label(id, id)} ${values[id]} mm`;
  // A length measured along the arm, cut off where the measurement ends.
  const alongArm = (id: string, from: Point[]): Guide => {
    const route: Point[] = [...from, [elbowX, elbowY], [wristX, wristY], [wristX, wristY + h]];
    let left = values[id];
    const points: Point[] = [route[0]];
    for (let i = 1; i < route.length && left > 0; i++) {
      const [x0, y0] = route[i - 1];
      const [x1, y1] = route[i];
      const length = Math.hypot(x1 - x0, y1 - y0);
      const t = Math.min(1, left / (length || 1));
      points.push([x0 + (x1 - x0) * t, y0 + (y1 - y0) * t]);
      left -= length;
    }
    const end = points[points.length - 1];
    return { id, label: mm(id), path: `M ${points.map((point) => point.join(" ")).join(" L ")} M ${end[0] - 12} ${end[1]} H ${end[0] + 12}`, labelX: end[0] + 14, labelY: end[1] };
  };
  const neckHalf = (shape.neck ?? 400) / Math.PI / 2;
  if (has("neck")) guides.push(across("neck", shoulderY - h * 0.08, values.neck));
  if (has("chest")) guides.push(across("chest", chestLineY, values.chest));
  if (has("naturalWaist")) guides.push(across("naturalWaist", waistY, values.naturalWaist));
  if (has("trouserWaist")) guides.push(across("trouserWaist", waistY + h * 0.15, values.trouserWaist));
  if (has("seat")) guides.push(across("seat", seatY, values.seat));
  if (has("halfBack") && !side) guides.push({ id: "halfBack", label: mm("halfBack"), path: `M 0 ${napeY + 150} H ${values.halfBack} M ${values.halfBack} ${napeY + 138} V ${napeY + 162}`, labelX: values.halfBack + 14, labelY: napeY + 156 });
  if (has("shoulder") && !side) guides.push({ id: "shoulder", label: mm("shoulder"), path: `M ${neckHalf} ${shoulderY - 6} L ${neckHalf + values.shoulder} ${shoulderY - 6} M ${neckHalf + values.shoulder} ${shoulderY - 18} V ${shoulderY + 6}`, labelX: neckHalf + values.shoulder + 14, labelY: shoulderY - 20 });
  if (has("armCircumference")) {
    const y = shoulderY + (elbowY - shoulderY) * 0.35;
    const x = armX + (elbowX - armX) * 0.35;
    const r = upperArmWidth / 2;
    guides.push({ id: "armCircumference", label: mm("armCircumference"), path: `M ${x - r} ${y} H ${x + r}`, labelX: x + r + 14, labelY: y });
  }
  for (const id of ["cuffSize", "wrist", "closeWrist"].filter(has)) {
    const r = values[id] / Math.PI / 2 * (side ? 0.6 : 1);
    guides.push({ id, label: mm(id), path: `M ${wristX - r} ${wristY} H ${wristX + r}`, labelX: wristX + r + 14, labelY: wristY + 20 });
  }
  const gutter = Math.max(chestHalfWidth, hipHalfWidth, shoulderHalfWidth) + 40;
  const lengths = ["scyeDepth", "backNeckToWaist", "naturalWaistLength", "shirtLength", "jacketLength", "garmentLength", "finishedLength", "coatLength", "overcoatLength", "capeLength"].filter(has);
  lengths.forEach((id, index) => guides.push(drop(id, -gutter - index * 28, values[id])));
  // Sleeves from the shoulder point, and shirt / two-piece sleeves from the nape.
  for (const id of ["sleeveLength", "onePieceSleeveLength", "shortSleeveLength", "shortOnePieceSleeveLength", "elbowLength"].filter(has)) guides.push(alongArm(id, [[side ? armX : shoulderHalfWidth, shoulderY]]));
  for (const id of ["shirtSleeveLength", "twoPieceSleeveLength"].filter(has)) guides.push(alongArm(id, [[0, napeY], [side ? armX : shoulderHalfWidth, shoulderY]]));
  if (has("insideLeg")) guides.push({ id: "insideLeg", label: mm("insideLeg"), path: `M ${kneeHalfWidth * 0.2} ${crotchY} V ${crotchY + values.insideLeg}`, labelX: kneeHalfWidth * 0.2 + 14, labelY: crotchY + values.insideLeg / 2 });
  if (has("trouserSideSeam")) guides.push({ id: "trouserSideSeam", label: mm("trouserSideSeam"), path: `M ${-hipHalfWidth - 30} ${waistY} V ${waistY + values.trouserSideSeam} M ${-hipHalfWidth - 42} ${waistY} H ${-hipHalfWidth - 18}`, labelX: -hipHalfWidth - 30, labelY: waistY - 12, centred: true });
  for (const id of ["trouserBottomWidth", "jeansBottomWidth"].filter(has)) guides.push({ id, label: mm(id), path: `M ${ankleHalfWidth - values[id] / 2} ${ground - h * 0.2} H ${ankleHalfWidth + values[id] / 2}`, labelX: ankleHalfWidth, labelY: ground - h * 0.2 - 12, centred: true });
  if (has("bodyRise")) guides.push({ id: "bodyRise", label: mm("bodyRise"), path: `M ${hipHalfWidth + 30} ${crotchY - values.bodyRise} V ${crotchY}`, labelX: hipHalfWidth + 44, labelY: crotchY - values.bodyRise / 2 });

    return (
      <svg viewBox={`-360 0 720 ${canvasHeight}`} role="img" aria-label={`${props.sex} mannequin construction, ${props.view} view`}>
      <g class="head-grid">
        <For each={landmarks}>{(landmark) => <>
          <line x1="-250" y1={atHead(landmark.count)} x2="250" y2={atHead(landmark.count)} />
          <text x="-242" y={atHead(landmark.count) - 8}>{landmark.count}H · {landmark.label}</text>
        </>}</For>
        <For each={Array.from({ length: Math.ceil(measurement.height / h) + 1 }, (_, index) => index)}>{(index) =>
          <line class="head-grid-tick" x1="-262" y1={atHead(index)} x2="-250" y2={atHead(index)} />
        }</For>
      </g>
      <line class="mannequin-floor" x1="-320" y1={ground} x2="320" y2={ground} />
      <line class="mannequin-height-line configured-line" x1="0" y1={top} x2="0" y2={ground} />
      <path class="mannequin-height-cap configured-line" d={`M -16 ${top} H 16 M -16 ${ground} H 16`} />
      <text class="dimension-label" x="14" y={ground - 16}>{measurement.height} HEIGHT</text>

      <line class="mannequin-reference-line" x1="-290" y1={top} x2="-290" y2={top + 2000} />
      <path class="mannequin-reference-line" d={`M -306 ${top} H -274 M -306 ${top + 2000} H -274`} />
      <text class="reference-label" x="-276" y={top + 1980}>2000 REF</text>

      <ellipse class="cranium-sphere" cx="0" cy={sphereCenterY} rx={sphereRadiusX} ry={sphereRadiusY} />
      <path class="mannequin-shape head-outline" d={headOutline} />
      {!side && <>
        <line class="mannequin-guide" x1={-sidePlaneX} y1={top + craniumHeight * 0.2} x2={-sidePlaneX} y2={jawTop} />
        <line class="mannequin-guide" x1={sidePlaneX} y1={top + craniumHeight * 0.2} x2={sidePlaneX} y2={jawTop} />
      </>}
      <line class="mannequin-guide" x1={-sidePlaneX} y1={sphereCenterY} x2={sidePlaneX} y2={sphereCenterY} />
      <line class="mannequin-guide" x1="0" y1={top} x2="0" y2={chinY} />

      {side && <g class={`side-face-details ${female ? "female" : "male"}`}>
        <path class="brow" d={female
          ? `M ${-hs * 0.35} ${top + h * 0.375} Q ${-hs * 0.27} ${top + h * 0.35} ${-hs * 0.17} ${top + h * 0.38}`
          : `M ${-hs * 0.36} ${top + h * 0.38} Q ${-hs * 0.27} ${top + h * 0.36} ${-hs * 0.16} ${top + h * 0.385}`} />
        <path class="eye" d={`M ${-hs * 0.33} ${top + h * 0.445} Q ${-hs * 0.29} ${top + h * (female ? 0.415 : 0.425)} ${-hs * 0.21} ${top + h * 0.44} Q ${-hs * 0.28} ${top + h * 0.47} ${-hs * 0.33} ${top + h * 0.445} Z`} />
        <circle class="iris" cx={-hs * 0.3} cy={top + h * 0.443} r={h * (female ? 0.017 : 0.014)} />
        {female && <path class="lashes" d={`M ${-hs * 0.33} ${top + h * 0.443} l ${-hs * 0.035} ${-h * 0.012} M ${-hs * 0.315} ${top + h * 0.432} l ${-hs * 0.03} ${-h * 0.02}`} />}
        <path class="nostril" d={`M ${-hs * f(0.43, 0.4)} ${top + h * 0.585} Q ${-hs * f(0.38, 0.36)} ${top + h * 0.565} ${-hs * f(0.36, 0.34)} ${top + h * 0.6}`} />
        <path class="lips" d={`M ${-hs * 0.37} ${top + h * 0.69} Q ${-hs * 0.33} ${top + h * 0.695} ${-hs * 0.3} ${top + h * 0.69}`} />
        <ellipse class="ear" cx={hs * 0.16} cy={top + h * 0.53} rx={hs * (female ? 0.075 : 0.09)} ry={h * (female ? 0.11 : 0.13)} />
        <path class="ear-inner" d={`M ${hs * 0.13} ${top + h * 0.47} Q ${hs * 0.2} ${top + h * 0.5} ${hs * 0.15} ${top + h * 0.58}`} />
      </g>}
      {props.view === "front" && <g class={`front-face-details ${female ? "female" : "male"}`}>
        <For each={[-1, 1]}>{(side) => <>
          <ellipse class="ear" cx={side * sp * 1.04} cy={top + h * 0.53} rx={hs * (female ? 0.05 : 0.06)} ry={h * (female ? 0.1 : 0.115)} />
          <path class="brow" d={female
            ? `M ${side * hs * 0.08} ${top + h * 0.405} Q ${side * hs * 0.2} ${top + h * 0.355} ${side * hs * 0.32} ${top + h * 0.395}`
            : `M ${side * hs * 0.07} ${top + h * 0.4} Q ${side * hs * 0.19} ${top + h * 0.375} ${side * hs * 0.32} ${top + h * 0.39}`} />
          <path class="eye" d={`M ${side * hs * 0.1} ${top + h * 0.46} Q ${side * hs * 0.19} ${top + h * (female ? 0.42 : 0.43)} ${side * hs * (female ? 0.3 : 0.29)} ${top + h * 0.455} Q ${side * hs * 0.19} ${top + h * (female ? 0.49 : 0.48)} ${side * hs * 0.1} ${top + h * 0.46} Z`} />
          <circle class="iris" cx={side * hs * 0.195} cy={top + h * 0.457} r={h * (female ? 0.022 : 0.019)} />
          {female && <path class="lashes" d={`M ${side * hs * (female ? 0.3 : 0.29)} ${top + h * 0.455} l ${side * hs * 0.035} ${-h * 0.018}`} />}
        </>}</For>
        <path class="nose" d={female
          ? `M ${-hs * 0.035} ${top + h * 0.47} Q ${-hs * 0.05} ${top + h * 0.56} ${-hs * 0.065} ${top + h * 0.6} M ${-hs * 0.08} ${top + h * 0.615} Q 0 ${top + h * 0.645} ${hs * 0.08} ${top + h * 0.615}`
          : `M ${-hs * 0.04} ${top + h * 0.46} L ${-hs * 0.06} ${top + h * 0.6} M ${-hs * 0.1} ${top + h * 0.615} Q ${-hs * 0.08} ${top + h * 0.645} ${-hs * 0.035} ${top + h * 0.635} Q 0 ${top + h * 0.65} ${hs * 0.035} ${top + h * 0.635} Q ${hs * 0.08} ${top + h * 0.645} ${hs * 0.1} ${top + h * 0.615}`} />
        <path class="lips" d={female
          ? `M ${-hs * 0.14} ${top + h * 0.735} Q ${-hs * 0.06} ${top + h * 0.7} 0 ${top + h * 0.715} Q ${hs * 0.06} ${top + h * 0.7} ${hs * 0.14} ${top + h * 0.735} Q ${hs * 0.06} ${top + h * 0.79} 0 ${top + h * 0.79} Q ${-hs * 0.06} ${top + h * 0.79} ${-hs * 0.14} ${top + h * 0.735} Z M ${-hs * 0.14} ${top + h * 0.735} Q 0 ${top + h * 0.75} ${hs * 0.14} ${top + h * 0.735}`
          : `M ${-hs * 0.15} ${top + h * 0.735} Q 0 ${top + h * 0.75} ${hs * 0.15} ${top + h * 0.735} M ${-hs * 0.08} ${top + h * 0.775} Q 0 ${top + h * 0.785} ${hs * 0.08} ${top + h * 0.775}`} />
        {!female && <path class="chin" d={`M ${-hs * 0.07} ${top + h * 0.9} Q 0 ${top + h * 0.925} ${hs * 0.07} ${top + h * 0.9}`} />}
      </g>}
      {props.view === "back" && <g class="front-face-details">
        <For each={[-1, 1]}>{(side) => <ellipse class="ear" cx={side * sp * 1.04} cy={top + h * 0.53} rx={hs * (female ? 0.05 : 0.06)} ry={h * (female ? 0.1 : 0.115)} />}</For>
      </g>}
      <g class={`hair ${female ? "female" : "male"}`}>
        <path d={hair} />
        <For each={hairStrands}>{(d) => <path class="hair-strand" d={d} />}</For>
        {ponytail && <g class="ponytail"><path d={ponytail} /><path class="hair-strand" d={side
        ? `M ${hs * 0.6} ${top + h * 0.3} Q ${hs * 0.82} ${top + h * 0.7} ${hs * 0.66} ${top + h * 1.25}`
        : `M 0 ${top + h * 0.36} Q ${-hs * 0.08} ${top + h * 0.9} ${hs * 0.01} ${top + h * 1.45}`} /></g>}
        {hairTie && <ellipse class="hair-tie" cx={hairTie[0]} cy={hairTie[1]} rx={h * 0.045} ry={h * 0.035} />}
      </g>

      <path class="mannequin-shape neck-form" d={side
        ? `M ${-headSpan * 0.1} ${chinY - h * 0.02} Q ${-h * (female ? 0.1 : 0.15)} ${atHead(1.2)} ${-h * (female ? 0.16 : 0.21)} ${shoulderY} L ${h * (female ? 0.2 : 0.26)} ${shoulderY} Q ${h * (female ? 0.17 : 0.22)} ${atHead(1.18)} ${headSpan * 0.22} ${top + h * 0.72} L ${jawX} ${top + h * 0.82} Q 0 ${top + h * 0.96} ${-headSpan * 0.1} ${chinY - h * 0.02} Z`
        : `M ${-headSpan * (female ? 0.21 : 0.25)} ${chinY - h * 0.08} L ${-h * (female ? 0.19 : 0.24)} ${shoulderY} L ${h * (female ? 0.19 : 0.24)} ${shoulderY} L ${headSpan * (female ? 0.21 : 0.25)} ${chinY - h * 0.08} Z`} />

      <line class="mannequin-landmark" x1={-shoulderHalfWidth} y1={shoulderY} x2={shoulderHalfWidth} y2={shoulderY} />
      <circle class="landmark-dot" cx={-shoulderHalfWidth} cy={shoulderY} r="7" />
      <circle class="landmark-dot" cx={shoulderHalfWidth} cy={shoulderY} r="7" />
      <text class="dimension-label" x={-shoulderHalfWidth} y={shoulderY - 15}>SHOULDER</text>

      <path class="mannequin-shape chest-egg" d={chestPath} />
      <path class={`trapezius-construction ${props.view}`} d={side
        ? `M ${-h * 0.16} ${chinY - h * 0.02} Q ${h * 0.05} ${atHead(1.24)} ${shoulderHalfWidth} ${shoulderY} L ${h * 0.16} ${atHead(1.72)} L ${h * 0.16} ${chinY - h * 0.04} Z`
        : props.view === "back"
          ? `M ${-h * 0.22} ${chinY - h * 0.04} Q ${-h * 0.45} ${atHead(1.2)} ${-shoulderHalfWidth * 0.88} ${shoulderY} L ${-h * 0.3} ${atHead(1.92)} L 0 ${atHead(2.12)} L ${h * 0.3} ${atHead(1.92)} L ${shoulderHalfWidth * 0.88} ${shoulderY} Q ${h * 0.45} ${atHead(1.2)} ${h * 0.22} ${chinY - h * 0.04} Z`
          : `M ${-h * 0.22} ${chinY - h * 0.04} Q ${-h * 0.4} ${atHead(1.24)} ${-shoulderHalfWidth * 0.82} ${shoulderY} L ${-h * 0.2} ${atHead(1.68)} L ${h * 0.2} ${atHead(1.68)} L ${shoulderHalfWidth * 0.82} ${shoulderY} Q ${h * 0.4} ${atHead(1.24)} ${h * 0.22} ${chinY - h * 0.04} Z`} />
      {female && props.view === "front" && <g class="female-chest-construction">
        <For each={[-1, 1]}>{(s) => <>
          <path d={`M ${s * chestHalfWidth * 0.12} ${bustY - h * 0.2}
            C ${s * chestHalfWidth * 0.5} ${bustY - h * 0.3} ${s * chestHalfWidth * 0.98} ${bustY - h * 0.16} ${s * chestHalfWidth * 0.94} ${bustY + h * 0.04}
            C ${s * chestHalfWidth * 0.9} ${bustY + h * 0.24} ${s * chestHalfWidth * 0.3} ${bustY + h * 0.26} ${s * chestHalfWidth * 0.1} ${bustY + h * 0.12}
            Q ${s * chestHalfWidth * 0.04} ${bustY - h * 0.06} ${s * chestHalfWidth * 0.12} ${bustY - h * 0.2} Z`} />
          <circle class="bust-apex" cx={s * chestHalfWidth * 0.5} cy={bustY} r={h * 0.025} />
        </>}</For>
      </g>}
      {female && props.view === "side" && <path class="female-chest-profile" d={`M ${-chestHalfWidth * 0.8} ${bustY - h * 0.32}
        C ${-chestHalfWidth * 1.1} ${bustY - h * 0.16} ${-chestHalfWidth * 1.42} ${bustY - h * 0.04} ${-chestHalfWidth * 1.36} ${bustY + h * 0.08}
        Q ${-chestHalfWidth * 1.26} ${bustY + h * 0.26} ${-chestHalfWidth * 0.86} ${bustY + h * 0.24} Z`} />}
      <path class="shoulder-girdle" d={side
        ? `M ${-h * 0.18} ${shoulderY} Q 0 ${shoulderY - h * 0.1} ${shoulderHalfWidth} ${shoulderY}`
        : `M ${-shoulderHalfWidth} ${shoulderY} Q ${-h * 0.48} ${shoulderY + h * 0.12} 0 ${shoulderY - h * 0.06} Q ${h * 0.48} ${shoulderY + h * 0.12} ${shoulderHalfWidth} ${shoulderY}`} />
      {props.view === "back" && <>
        <g class="lat-construction">
          <path d={`M ${-h * 0.78} ${atHead(1.78)} Q ${-h * 0.88} ${atHead(2.28)} ${-waistHalfWidth * 0.52} ${waistY} Q ${-h * 0.28} ${atHead(2.5)} ${-h * 0.24} ${atHead(1.88)} Z`} />
          <path d={`M ${h * 0.78} ${atHead(1.78)} Q ${h * 0.88} ${atHead(2.28)} ${waistHalfWidth * 0.52} ${waistY} Q ${h * 0.28} ${atHead(2.5)} ${h * 0.24} ${atHead(1.88)} Z`} />
        </g>
        <g class="scapula-construction">
          <path d={`M ${-h * 0.22} ${atHead(1.72)} Q ${-h * 0.72} ${atHead(1.78)} ${-h * 0.62} ${atHead(2.45)} Q ${-h * 0.36} ${atHead(2.34)} ${-h * 0.22} ${atHead(1.72)} Z`} />
          <path d={`M ${h * 0.22} ${atHead(1.72)} Q ${h * 0.72} ${atHead(1.78)} ${h * 0.62} ${atHead(2.45)} Q ${h * 0.36} ${atHead(2.34)} ${h * 0.22} ${atHead(1.72)} Z`} />
        </g>
      </>}
      <line class="mannequin-guide" x1={-chestHalfWidth} y1={chestLineY} x2={chestHalfWidth} y2={chestLineY} />
      <text class="dimension-label" x={-chestHalfWidth} y={chestLineY - 14}>CHEST</text>

      <path class="mannequin-shape hip-bucket" d={hipPath} />
      {/* Sacral triangle: the two dimples over the back of the pelvis (set wider on the
          female figure) and the point where it narrows into the cleft of the seat. */}
      {props.view === "back" && <g class="sacrum-construction">
        <path d={`M ${-sacrumHalfWidth} ${sacrumTop}
          Q 0 ${sacrumTop - h * 0.04} ${sacrumHalfWidth} ${sacrumTop}
          Q ${sacrumHalfWidth * 0.5} ${sacrumTop + (sacrumApex - sacrumTop) * 0.55} 0 ${sacrumApex}
          Q ${-sacrumHalfWidth * 0.5} ${sacrumTop + (sacrumApex - sacrumTop) * 0.55} ${-sacrumHalfWidth} ${sacrumTop} Z`} />
        <path class="seat-cleft" d={`M 0 ${sacrumApex} Q ${h * 0.01} ${(sacrumApex + crotchY) / 2} 0 ${crotchY}`} />
        <For each={[-1, 1]}>{(s) => <circle cx={s * sacrumHalfWidth} cy={sacrumTop} r={h * 0.025} />}</For>
      </g>}
      <line class="mannequin-guide" x1={-waistHalfWidth} y1={waistY} x2={waistHalfWidth} y2={waistY} />

      <g class="limb-construction">
        <ArmForm mirror far={side} shoulder={[armX, shoulderY]} elbow={[elbowX, elbowY]} wrist={[wristX, wristY]} width={upperArmWidth} wristWidth={wristWidth} h={h} profile={armProfile} side={side} female={female} />
        {!side && <LegForm mirror hip={[thighX, crotchY]} knee={[kneeHalfWidth, kneeY]} ankle={[ankleHalfWidth, ankleY]} ground={ground} h={h} profile={legProfile} side={false} female={female} thighScale={thighScale} />}
        <LegForm hip={[thighX, crotchY]} knee={[kneeHalfWidth, kneeY]} ankle={[ankleHalfWidth, ankleY]} ground={ground} h={h} profile={legProfile} side={side} female={female} thighScale={thighScale} />
        {/* The near arm hangs in front of the leg in side view, so it is drawn last. */}
        <ArmForm shoulder={[armX, shoulderY]} elbow={[elbowX, elbowY]} wrist={[wristX, wristY]} width={upperArmWidth} wristWidth={wristWidth} h={h} profile={armProfile} side={side} female={female} />
      </g>
      <g class="joint-construction">
        <For each={[
          [armX, shoulderY], [elbowX, elbowY], [wristX, wristY], [thighX, crotchY], [kneeHalfWidth, kneeY], [ankleHalfWidth, ankleY],
          ...(!side ? [[-armX, shoulderY], [-elbowX, elbowY], [-wristX, wristY], [-thighX, crotchY], [-kneeHalfWidth, kneeY], [-ankleHalfWidth, ankleY]] : []),
        ]}>{(point) => <circle cx={point[0]} cy={point[1]} r={h * 0.075} />}</For>
        <For each={[
          [armX, shoulderY], [elbowX, elbowY], [wristX, wristY], [thighX, crotchY], [kneeHalfWidth, kneeY], [ankleHalfWidth, ankleY],
          ...(!side ? [[-armX, shoulderY], [-elbowX, elbowY], [-wristX, wristY], [-thighX, crotchY], [-kneeHalfWidth, kneeY], [-ankleHalfWidth, ankleY]] : []),
        ]}>{(point) => <path d={`M ${point[0] - h * 0.1} ${point[1]} H ${point[0] + h * 0.1} M ${point[0]} ${point[1] - h * 0.1} V ${point[1] + h * 0.1}`} />}</For>
      </g>

      {props.view === "back" && <line class="mannequin-detail" x1="0" y1={chinY} x2="0" y2={chestBottom} />}
      {props.view === "front" && <circle class="mannequin-detail" cx="0" cy={chestTop + chestHeight * 0.76} r="5" />}
      <g class="measurement-guides">
        <For each={guides}>{(guide) => <g classList={{ "measurement-guide": true, active: props.active === guide.id }}>
          <path d={guide.path} />
          <Show when={props.active === guide.id}><text x={guide.labelX} y={guide.labelY} text-anchor={guide.centred ? "middle" : "start"}>{guide.label}</text></Show>
        </g>}</For>
      </g>
      <text class="view-label" x="0" y={ground + 55} text-anchor="middle">{props.view.toUpperCase()} · {props.sex.toUpperCase()}</text>
      </svg>
    );
  });
  return <>{construction()}</>;
}
